import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Code from '@/images/Code.svg';
import InfoIcon from '@/images/Info.svg';
import theme from '@/theme';
import { TriggerWebhook } from '@d-id/types';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { WebhookAddButton } from './components';
import { WebhookItem } from './components/WebhookItem';
import { useWebhookComponentContext, WebhookComponentProvider } from './contex';

const WebhookComponentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;
    width: 100%;
`;

const WebhookComponentAddContainer = styled.div`
    display: flex;
    width: 100%;
    gap: 16px;
    width: 100%;
    justify-content: space-between;
`;

const WebhookComponentInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    color: ${theme.colors.black[700]};
    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const WebhookComponentInfoTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.28px;
`;

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 230px;

    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: -0.12px;

    p {
        display: flex;
        gap: 4px;
    }

    b {
        border-radius: 4px;
        padding: 4px 8px;
        background-color: ${theme.colors.secondary[700]};

        font-family: Menlo;
        font-size: 10px;
        font-style: normal;
        font-weight: 400;
        line-height: 130%;
        letter-spacing: -0.1px;
    }
`;

const WebhookComponent = () => {
    const { webhooks } = useWebhookComponentContext();

    return (
        <WebhookComponentContainer>
            <WebhookComponentAddContainer>
                <WebhookComponentInfo>
                    <WebhookComponentInfoTitle>
                        <MaskedIcon src={Code} size="24px" />
                        Post conversation Webhook
                        <Tooltip
                            placement="top"
                            arrow={true}
                            title={
                                <TooltipContent>
                                    <p>On conversation end we send JSON with:</p>
                                    <p>
                                        <b>chatID</b> chat identifier
                                    </p>
                                    <p>
                                        <b>ownerID</b> agent identifier
                                    </p>
                                    <p>
                                        <b>agentID</b> agent identifier
                                    </p>
                                    <p>
                                        <b>messages[]</b> ordered messages
                                    </p>
                                </TooltipContent>
                            }
                            slotProps={{
                                tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '16px', fontSize: '14px' } },
                                arrow: { sx: { color: theme.colors.secondary[900] } },
                            }}>
                            <MaskedIcon src={InfoIcon} size="16px" />
                        </Tooltip>
                    </WebhookComponentInfoTitle>
                    Post the conversation transcript to your endpoint when it ends.
                </WebhookComponentInfo>
                <WebhookAddButton />
            </WebhookComponentAddContainer>

            {webhooks.map((webhook, index) => (
                <WebhookItem key={index + webhook.url} webhook={webhook} index={index} />
            ))}
        </WebhookComponentContainer>
    );
};

interface Props {
    webhooks: TriggerWebhook[];
    onSave: (triggerWebhook: TriggerWebhook | null, index: number) => void;
}

const WebhookComponentWrapper = ({ onSave, webhooks }: Props) => {
    return (
        <WebhookComponentProvider onSave={onSave} webhooks={webhooks}>
            <WebhookComponent />
        </WebhookComponentProvider>
    );
};

export { WebhookComponentWrapper as WebhookComponent };
